import { TextField } from "@material-ui/core";
import { AccountTree, BorderColorOutlined, ContentCopy, ExpandMore, Settings, TerminalOutlined } from "@mui/icons-material";
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WebAssetOutlinedIcon from '@mui/icons-material/WebAssetOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button, Chip,
  Dialog,
  IconButton,
  LinearProgress,
  TableCell,
  TableRow,
  Tooltip, Typography
} from "@mui/material";
import copy from "copy-to-clipboard";
import { debounce } from "lodash";
import moment from "moment";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getConfigs } from "../../../../configs";
import { DocumentText } from "../../../../icons/document-text";
import { ExternalLink } from "../../../../icons/external-link";
import { Refresh } from "../../../../icons/refresh";
import { axiosInstance } from "../../../../services/axios";
import { IAlertState, useAlertState } from "../../../../store/alertState";
import { ApiModalContent } from "../../../assets/ApiModal";
import RunHistoryPopup from "../../../assets/apiComponents/runHistoryPopup";
import Breadcrumb from "../../../common/breadcrumb";
import CommonPopper from "../../../common/commonPopper";
import Metrics from "../../../common/metrics";
import ModalWrapper from "../../../common/modal/wrapper";
import ToolInfo from "../../../common/toolInfo";
import AddJobSpecificationsForm from "../../../jobs/AddJobSpecifications/addJobSpecificationsForm";
import { Labels } from "./labels";
import SequenceGraphPopup from "./sequenceGraphPopup";
export interface IDataTile {
  title: string;
  ComponentToRender?: FC<any>;
  description?: string;
  hoverText?: string;
  onClick?: () => void;
  xs?: number;
  md?: number;
}

const ErrorAccordion: FC<{ result: any }> = ({ result }) => {
  if (result.state !== "errored" || !result.errors || result.errors.length === 0) {
    return null;
  }

  return (
    <Accordion sx={{ m: 2, border: "1px solid red" }} defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6" color="error">Errors</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {result.errors.map((error: any, index: number) => (
          <Alert key={index} severity="error" sx={{ mb: 1 }}>
            {error?.message}
          </Alert>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};


export const FindingDataRow: FC<IDataTile> = ({
  title,
  ComponentToRender,
  description,
  hoverText = undefined,
  onClick,
  xs,
  md,
}) => {
  return (
    <TableRow>
      <TableCell>

        <Typography variant={"overline"} sx={{}}>
          {title}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          className={onClick || hoverText ? "hand" : ""}
          sx={{ width: "fit-content", opacity: 0.6 }}
          variant={"h6"}
          onClick={onClick ? onClick : () => { }}
        >
          {onClick ? <ExternalLink fontSize="inherit" /> : null}
          {hoverText ? <Tooltip title={hoverText}><Box sx={{ width: "fit-content" }}>{description}</Box></Tooltip> : description}
          {ComponentToRender ? <ComponentToRender /> : null}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export const ResultData: FC<any> = ({
  id,
  result,
  api,
  onFaultsGroupChange,
  selectedTab,
  setSelectedTab,
  Summary,
  riskIssueData
}: any) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openDefinitionModal, setOpenDefinitionModal] = useState(false);
  const [resultDataTiles, setResultDataTiles] = useState([] as IDataTile[]);
  const [resultName, setResultName] = useState(result?.name);
  const [saveNameText, setSaveNameText] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const { setMessage } = useAlertState() as IAlertState;
  const navigate = useHistory();
  const breadcrumbsData = useMemo(() => [{
    label: result?.projectName,
    icon: <AccountTree fontSize="inherit" />,
    onClick: () => window.open(`/projects/${result?.projectId}`, "_self"),
    tooltip: "Project"
  }, {
    label: result?.environmentName,
    icon: <TerminalOutlined fontSize="inherit" />,
    onClick: () => window.open(`/projects/${result?.projectId}?assetid=${result?.assetId}`, "_self"),
    tooltip: "Environment"
  },
  {
    label: result?.assetName,
    icon: <WebAssetOutlinedIcon fontSize="inherit" />,
    onClick: () => window.open(`/projects/${result?.projectId}?assetid=${result?.assetId}`, "_self"),
    tooltip: "Asset"
  }], [result?.assetId, result?.assetName, result?.environmentName, result?.projectId, result?.projectName])
  useEffect(() => {
    if (result) {
      setResultName(result?.name);
      const xs = 12;
      const md = 4;
      const data: IDataTile[] = [
        {
          title: "API",
          description: result.assetName,
          onClick: handleOpenDefinitionModal,
          xs,
          md,
        },
        {
          title: "Environment",
          description: result.environmentName,
          xs,
          md,
        },
        {
          title: "Updated At",
          description: moment(result?.updatedAt).format("DD MMM YYYY h:mm a"),
          xs,
          md,
        },
        {
          title: "Duration",
          description: `${moment
            .duration(result?.metrics?.duration, "milliseconds")
            .minutes()} m ${moment
              .duration(result?.metrics?.duration, "milliseconds")
              .seconds()} s`,
          xs,
          md,
        },
        {
          title: "Sequences",
          ComponentToRender: () => (
            <SequenceGraphPopup resultId={id} metrics={result?.metrics} />
          ),
          xs,
          md,
        },
        {
          title: "Requests",
          description: result?.metrics?.requestsCount,
          xs,
          md,
        },
        {
          title: "Issues",
          description: result?.metrics?.issuesCount,
          xs,
          md,
        },
        {
          title: "New Issues",
          description: result?.metrics?.issuesCountNew,
          xs,
          md,
        },
        {
          title: "Configuration",
          onClick: handleDialogOpen,
          xs,
          md,
        },
      ];
      setResultDataTiles(data);
    }
  }, [result]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleOpenDefinitionModal = () => {
    setOpenDefinitionModal(true);
  };

  const handleCloseDefinitionModal = () => {
    setOpenDefinitionModal(false);
  };

  const saveName = useCallback(
    debounce(async (name: string) => {
      try {
        await axiosInstance.patch(
          `${getConfigs().baseApiUrl}/results/${result.id}`,
          {
            checkpoint: name?.trim() !== "",
            name: name?.trim() || "",
          }
        );

        setMessage({ title: 'Run has been updated', type: "success" })
        navigate.push(`/findings?resultId=${result.id}`)
      } catch (error) { }
    }, 500),
    [result]
  );

  const handleEndSaveNameText = useCallback(
    debounce(() => {
      setTimeout(() => {
        setSaveNameText("( Saved )");
        setTimeout(() => {
          setSaveNameText("");
        }, 4000);
      }, 1000);
    }, 500),
    []
  );

  const downloadPDF = async (id: string) => {
    setIsDownloading(true);
    try {
      const element = document.createElement("a");
      const response = await axiosInstance.get(
        `${getConfigs().baseApiUrl}/reports?resultId=${id}`,
        {
          responseType: "blob",
          timeout: 60000,
        }
      );
      const blob = response?.data;
      const file = new Blob([blob], {
        type: "application/octet-stream",
      });
      element.href = URL.createObjectURL(file);
      element.download = `run_${id}.pdf`;
      document.body.appendChild(element);
      element.click();
    } catch (error: any) {
      console.log(error);
    }
    setIsDownloading(false);
  };

  return (
    <>
      <Dialog maxWidth="lg" onClose={handleDialogClose} open={dialogOpen}>
        <Box sx={{ m: 3 }}>
          id: {id}
          result.id : {result.id}
          <AddJobSpecificationsForm apiId={result?.assetId} preventRedirect configId={id} source="run" isReadOnly={true} />
        </Box>
      </Dialog>


      <Box sx={{ px: 2, mt: 1, display: "flex", alignItems: "center", width: "100%", justifyContent: 'space-between' }}>
        <Breadcrumb data={breadcrumbsData} />
        <Box display={'flex'} sx={{ alignItems: 'center', mr: 3 }}>
          <Box display={'flex'} sx={{ alignItems: 'center'}}>
            <Tooltip title='Tool'>
             
                <ToolInfo name={`${result?.toolInfo?.name} ${result?.toolInfo?.version ? "(" + result?.toolInfo?.version + ")" : ""}`} />
              
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: 1, display: "flex", alignItems: "flex-start", width: "100%", justifyContent: 'space-between' }}>
        <Box sx={{ display: "flex", alignItems: 'center' }}>
          <Chip size='small' sx={{ mr: 2 }} label={result?.toolInfo?.type} />
          <Typography variant="h5"
            sx={{ display: "flex", alignItems: 'center', fontSize: '1.25em', mr: 2 }}
            color="text.primary">
            Run {result && result?.name ? result?.name : result?.id ? `#${result?.id?.split("-")[0]}` : null}

          </Typography>
          <CommonPopper
            elevatePopper
            placement={'bottom-start'}
            trigger={
              <Chip
                className="hand"
                size="small"
                label="ID"
                sx={{ mr: 2 }}
              />}

            child={
              <Box sx={{ p: 2 }}>
                <Tooltip title="Click to copy"
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'zIndex',
                        enabled: true,
                        phase: 'write',
                        fn({ state }) {
                          state.styles.popper.zIndex = '99999';
                        },
                      },
                    ],
                  }}
                ><pre className="hand" onClick={() => {
                  copy(result?.id);
                  setMessage({
                    title: "Project ID is copied to the clipboard",
                    type: "success",
                  });
                }}><ContentCopy fontSize="inherit" />
                    {result?.id
                    } </pre>
                </Tooltip>
              </Box>
            }
          />


          <ModalWrapper
            maxWidth="sm"
            title={`Edit Run`}
            trigger={
              <Tooltip title='Edit'>
                <IconButton size='small' sx={{ color: 'text.primary', borderColor: 'neutral.300' }}>
                  <BorderColorOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            }
            child={
              <Box sx={{ m: 2, ml: 0, mb: 1 }}>
                <TextField
                  label={`Name`}
                  size="small"
                  value={resultName || ""}
                  helperText="Naming a run turns it into a checkpoint"
                  onChange={({ target }) => {
                    setResultName(target.value);
                  }}
                >

                </TextField>
                <Box>
                  <Button variant='contained' size='small' sx={{ color: 'primary', borderColor: 'neutral.300', m: 2, mt: 4, mb: 0 }} onClick={() => saveName(resultName)}>
                    Save
                  </Button>
                </Box>
              </Box>
            }
          />
        </Box>

        <Box sx={{ alignItems: 'center', display: "flex", justifyContent: "end", mr:4 }}>
        <Box sx={{ display: "flex", alignItems: 'center', mr: 0.5 }}>
            <FiberManualRecordIcon
              sx={{ mr: 0.5 }}
              color={result?.state === "completed" ? "success" : result?.state === "errored" ? "error" : "warning"}
              fontSize="inherit" />
            <Typography variant="body2" sx={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              {result?.state?.charAt(0)?.toUpperCase() + result?.state?.slice(1)}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: 'center' }}>
            <Typography variant="caption" sx={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              {moment(result?.updatedAt).format("h:mm A DD MMM YYYY")}
              &nbsp;
            </Typography>
          </Box>

          <Box sx={{ display: "flex", mr: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}>

              {moment
                .duration(result?.metrics?.duration, "milliseconds")
                .minutes()}
              m&nbsp;
              {moment
                .duration(result?.metrics?.duration, "milliseconds")
                .seconds()}
              s
            </Typography>
          </Box>
         
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: 1, display: "flex", alignItems: "flex-start", width: "100%", justifyContent: 'space-between' }}>
        <Box display={'flex'} sx={{ alignItems: 'bottom' }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              height: "30px",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "4em",
                mr: 1,
                lineHeight: "1em",
                textAlign: "right",
              }}
            >
              {result?.metrics?.issuesCount}
            </Typography>

          </Box>
          <Box>
            <Box display={'flex'} sx={{alignItems: 'center'}}>
            <Typography
              variant="body1"
              sx={{
                textAlign: "left",
                my: 1,
                fontSize: "1em",
                fontWeight: "bold",
                lineHeight: "1em",
                
              }}
            >
              Issues
              
            </Typography>
            </Box>
            <Metrics sx={{mt:2}}data={riskIssueData} />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Box sx={{ display: "flex", justifyContent: "end", pr: 4 }}>

              
              {api?.assetType === "api" && (
                <SequenceGraphPopup resultId={id} metrics={result?.metrics} />
              )}
              <Box sx={{ ml: 1 }}>
                <Labels labels={result?.labels} color="default" />
              </Box>
              {/* {Summary && <Summary />} */}
              {(api?.assetType === "api") && <>

                <ModalWrapper
                  title="View Configuration"
                  maxWidth="lg"
                  trigger={<Tooltip title="Configuration"><IconButton sx={{ py: 0.5, height: "fit-content" }} ><DocumentText sx={{ mr: 1 }} /></IconButton></Tooltip>}
                  child={
                    <AddJobSpecificationsForm apiId={result?.assetId} preventRedirect configId={id} source="run" isReadOnly={true} />
                  }
                />


              </>}

              <Tooltip title={isDownloading ? "..." : "PDF report"}><IconButton sx={{ py: 0.5, height: "fit-content" }} onClick={() => !isDownloading && downloadPDF(id)}><PictureAsPdfIcon sx={{ mr: 1 }} /></IconButton></Tooltip>

              <RunHistoryPopup sx={{ width: "100%" }} assetId={result.assetId} toolInfo={result?.toolInfo} environmentId={result?.environmentId} />
              {api?.assetType === "api" && <ModalWrapper maxWidth="lg" title="Settings" trigger={<Tooltip title="Settings"><IconButton sx={{ py: 0.5, height: "fit-content" }}  ><Settings /></IconButton></Tooltip>} child={<ApiModalContent assetId={api?.id} />} />}


            </Box>
          </Box>

      </Box>


      {result?.state !== "completed" && <Box sx={{ display: "flex", justifyContent: "end", pb: 2, pr: 3 }}>
        <Box sx={{ display: "inline", mr: 2, pt: 1 }}></Box>
        <Box sx={{ pt: 1, display: "inline" }}>
          {result?.state !== "completed" ? (
            <Refresh
              sx={{ pl: 1, position: "relative", top: "0.25em" }}
              className="hand"
              fontSize="small"
              onClick={() => window.location.reload()}
            />
          ) : null}
        </Box>
      </Box>}




      <ErrorAccordion result={result} />
      <ResultProgress result={result} />

    </>
  );
};

const ResultProgress: FC<any> = ({ result }) => {
  return result.state && result?.state === "running" ? (
    <>
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This run is currently in progress. We've already{" "}
          <b>found {result?.metrics?.issuesCount} issues</b>. It'll take
          a few more moments to complete.
        </Typography>
        <LinearProgress />
        <Button
          variant="contained"
          size="small"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Alert>
    </>
  ) : <></>
};
